import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Check, Coins, Loader2, Zap, Trash2, Receipt, Download, TrendingUp, FileText, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ModelStatsCard from "@/components/ModelStatsCard";
import ReferralCard from "@/components/ReferralCard";
import SceneAnalyticsCard from "@/components/SceneAnalyticsCard";
import ShareClicksCard from "@/components/ShareClicksCard";
import ArtistProfileCard from "@/components/ArtistProfileCard";
import FollowerStatsCard from "@/components/FollowerStatsCard";
import MockupStatsCard from "@/components/MockupStatsCard";

const PLAN_FEATURES = {
  free: ["20 credits included", "MP3 + WAV export", "Basic commercial license", "Community support"],
  creator: ["200 credits / month", "Loopable tracks", "Stems export", "Perpetual license", "Email support"],
  pro: ["1,000 credits / month", "Priority rendering", "Projects & variations", "API access", "Usage analytics"],
  business: ["5,000 credits / month", "B2B REST API", "Adaptive game music", "Unity & Unreal SDK", "Dedicated support"],
};
const ORDER = ["free", "creator", "pro", "business"];

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [packs, setPacks] = useState(null);
  const [loadingKey, setLoadingKey] = useState("");
  const [topup, setTopup] = useState({ enabled: false, threshold: 100, pack: "pack_500" });
  const [savingTopup, setSavingTopup] = useState(false);
  const topupEdited = useRef(false);
  const [savingCard, setSavingCard] = useState(false);
  const [history, setHistory] = useState([]);
  const [histType, setHistType] = useState("all");
  const [histRange, setHistRange] = useState("all");
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchParams, setSearchParams] = useSearchParams();

  const [liveMode, setLiveMode] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open the subscription portal.");
      setPortalBusy(false);
    }
  };
  useEffect(() => {
    api.get("/billing/history").then(({ data }) => setHistory(data.items || [])).catch(() => {});
  }, [user?.credits]);
  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setLiveMode(data.mode === "live")).catch(() => {});
  }, []);

  const filteredHistory = useMemo(() => {
    const now = Date.now();
    const days = histRange === "all" ? null : Number(histRange);
    return history.filter((h) => {
      if (histType !== "all" && h.kind !== histType) return false;
      if (days && h.date && now - new Date(h.date).getTime() > days * 86400000) return false;
      return true;
    });
  }, [history, histType, histRange]);

  const spend = useMemo(() => {
    const map = {};
    filteredHistory.forEach((h) => {
      if (!h.date) return;
      const k = h.date.slice(0, 7);
      const m = map[k] || (map[k] = { real: 0, demo: 0 });
      if (h.mode === "real") m.real += h.amount; else m.demo += h.amount;
    });
    const now = new Date();
    const out = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ month: k, ...(map[k] || { real: 0, demo: 0 }) });
    }
    return out;
  }, [filteredHistory]);

  const monthRealSpend = useMemo(() => {
    const k = new Date().toISOString().slice(0, 7);
    return history.filter((h) => h.mode === "real" && h.date && h.date.slice(0, 7) === k)
      .reduce((s, h) => s + h.amount, 0);
  }, [history]);

  const exportHistoryCsv = () => {
    if (!filteredHistory.length) return;
    const header = ["Date", "Type", "Description", "Credits", "Amount", "Currency", "Mode", "PaymentID"];
    const body = filteredHistory.map((h) => [h.date, h.kind, h.description, h.credits, h.amount.toFixed(2), h.currency, h.mode, h.payment_intent_id || ""]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header, ...body].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "musicflow-billing-history.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Billing history exported as CSV.");
  };

  const downloadStatement = async () => {
    try {
      const res = await fetch(`${API}/billing/statement?month=${stmtMonth}`, { credentials: "include" });
      if (!res.ok) throw new Error("stmt");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `musicflow-statement-${stmtMonth}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Monthly statement downloaded.");
    } catch { toast.error("Could not generate statement."); }
  };

  const emailStatement = async () => {
    try { await api.post(`/billing/statement/email?month=${stmtMonth}`); toast.success(`Statement emailed to ${user?.email}.`); }
    catch { toast.error("Could not email statement."); }
  };

  useEffect(() => {
    const setupSession = searchParams.get("setup_session");
    if (searchParams.get("setup_cancel")) {
      toast.info("Card setup cancelled.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (setupSession) {
      api.get(`/billing/save-card/status/${setupSession}`).then(({ data }) => {
        if (data.saved) { toast.success(`Card •••• ${data.last4} saved for auto top-up.`); refreshUser(); }
      }).catch(() => {}).finally(() => setSearchParams({}, { replace: true }));
    }
  }, [searchParams, setSearchParams, refreshUser]);

  const saveCard = async () => {
    setSavingCard(true);
    try {
      const { data } = await api.post("/billing/save-card", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start card setup.");
      setSavingCard(false);
    }
  };

  const removeCard = async () => {
    try { await api.delete("/billing/card"); await refreshUser(); toast.success("Saved card removed."); }
    catch { toast.error("Could not remove card."); }
  };

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      setPlans(data.plans);
      setPacks(data.credit_packs);
    });
    api.get("/billing/auto-topup").then(({ data }) => { if (!topupEdited.current) setTopup(data); }).catch(() => {});
    refreshUser();
  }, [refreshUser]);

  const saveTopup = async (next) => {
    topupEdited.current = true;
    const merged = { ...topup, ...next };
    setTopup(merged);
    setSavingTopup(true);
    try {
      const { data } = await api.post("/billing/auto-topup", merged);
      setTopup(data);
      toast.success(data.enabled ? "Auto top-up enabled." : "Auto top-up disabled.");
    } catch {
      toast.error("Could not save auto top-up.");
    } finally { setSavingTopup(false); }
  };

  const checkout = async (lookup_key) => {
    if (!lookup_key) return;
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout.");
      setLoadingKey("");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <header className="mb-8">
        <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
          <CreditCard className="text-primary" /> Billing & Credits
        </h1>
        <p className="text-muted-foreground mt-1">You're on the <span className="text-primary font-semibold capitalize">{user?.plan}</span> plan with <span className="font-mono font-bold text-foreground">{user?.credits}</span> credits.</p>
        {user?.plan && user.plan !== "free" && (
          <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={openPortal} disabled={portalBusy} data-testid="manage-subscription-btn">
            {portalBusy ? <Loader2 className="animate-spin" size={14} /> : <Settings2 size={14} />} Manage subscription
          </Button>
        )}
      </header>

      <section className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-12">
        {plans && ORDER.map((key, i) => {
          const p = plans[key];
          const current = user?.plan === key;
          const highlight = key === "pro";
          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`relative rounded-2xl border p-6 flex flex-col ${highlight ? "border-primary bg-primary/[0.04] glow-primary" : "border-border bg-card"}`}
              data-testid={`plan-card-${key}`}
            >
              {highlight && <Badge className="absolute -top-2.5 left-6 bg-primary text-primary-foreground">Most popular</Badge>}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <div className="mt-2 mb-4">
                <span className="font-display text-4xl font-extrabold">€{p.price}</span>
                {p.price > 0 && <span className="text-muted-foreground text-sm">/mo</span>}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {PLAN_FEATURES[key].map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-muted-foreground">
                    <Check size={16} className="text-primary shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                disabled={current || !p.lookup || loadingKey === p.lookup}
                onClick={() => checkout(p.lookup)}
                className={`${highlight ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted hover:bg-muted/70"} disabled:opacity-100 disabled:bg-muted/60 disabled:text-foreground/80 disabled:border disabled:border-border`}
                data-testid={`subscribe-${key}`}
              >
                {p.lookup && loadingKey === p.lookup ? <Loader2 className="animate-spin" size={16} /> : current ? "Current plan" : p.price === 0 ? "Free forever" : "Upgrade"}
              </Button>
            </motion.div>
          );
        })}
      </section>

      <section>
        <h2 className="font-display text-2xl font-extrabold mb-4 flex items-center gap-2"><Coins className="text-secondary" size={22} /> Top up credits</h2>
        <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
          {packs && Object.entries(packs).map(([key, pk]) => (
            <div key={key} className="rounded-2xl border border-border bg-card p-6 flex items-center justify-between" data-testid={`pack-card-${key}`}>
              <div>
                <div className="font-display text-lg font-bold">{pk.name}</div>
                <div className="text-muted-foreground text-sm">One-time · €{pk.price}</div>
              </div>
              <Button onClick={() => checkout(pk.lookup)} disabled={loadingKey === pk.lookup} variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary/10" data-testid={`buy-${key}`}>
                {loadingKey === pk.lookup ? <Loader2 className="animate-spin" size={16} /> : "Buy"}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 font-mono" data-testid="billing-payment-note">
          {liveMode ? "Secure payment by Stripe · cards, Apple Pay & Google Pay · cancel anytime" : "Test card: 4242 4242 4242 4242 · any future expiry · any CVC"}
        </p>
      </section>

      <section className="mt-12 max-w-xl">
        <h2 className="font-display text-2xl font-extrabold mb-4 flex items-center gap-2"><Zap className="text-primary" size={22} /> Auto top-up</h2>
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="auto-topup-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-lg font-bold">Never run out mid-session</div>
              <p className="text-sm text-muted-foreground mt-0.5">Automatically add credits when your pool drops below a threshold — so teammates generating into your shared projects never get blocked.</p>
            </div>
            <Switch checked={topup.enabled} onCheckedChange={(v) => saveTopup({ enabled: v })} disabled={savingTopup} data-testid="auto-topup-toggle" />
          </div>

          <div className={`grid sm:grid-cols-3 gap-4 mt-5 transition-opacity ${topup.enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">When credits drop below</span>
              <Input
                type="number" min={0} value={topup.threshold}
                onChange={(e) => setTopup((t) => ({ ...t, threshold: Number(e.target.value) }))}
                onBlur={() => saveTopup({ threshold: topup.threshold })}
                className="bg-background border-border font-mono" data-testid="auto-topup-threshold"
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">Add this pack</span>
              <Select value={topup.pack} onValueChange={(v) => saveTopup({ pack: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="auto-topup-pack"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">
                  {packs && Object.entries(packs).map(([key, pk]) => (
                    <SelectItem key={key} value={key}>{pk.name} · €{pk.price}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">Monthly cap € (0 = none)</span>
              <Input
                type="number" min={0} value={topup.monthly_cap ?? 0}
                onChange={(e) => setTopup((t) => ({ ...t, monthly_cap: Number(e.target.value) }))}
                onBlur={() => saveTopup({ monthly_cap: topup.monthly_cap ?? 0 })}
                className="bg-background border-border font-mono" data-testid="auto-topup-cap"
              />
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-4" data-testid="payment-method-row">
            <div className="flex items-center gap-2.5 min-w-0">
              <CreditCard size={18} className={user?.has_card ? "text-primary" : "text-muted-foreground"} />
              <div className="min-w-0">
                <div className="text-sm font-medium">Payment method</div>
                <div className="text-xs text-muted-foreground truncate" data-testid="card-status">
                  {user?.has_card ? `${(user.card_brand || "card").toUpperCase()} •••• ${user.card_last4} — real charges enabled` : "No card saved — top-ups run in demo mode"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {user?.has_card && (
                <Button variant="ghost" size="icon" onClick={removeCard} className="text-muted-foreground hover:text-destructive h-8 w-8" data-testid="remove-card"><Trash2 size={15} /></Button>
              )}
              <Button onClick={saveCard} disabled={savingCard} variant="outline" className="border-primary/40 text-primary hover:bg-primary/10" data-testid="save-card-button">
                {savingCard ? <Loader2 className="animate-spin" size={15} /> : user?.has_card ? "Update card" : "Save a card"}
              </Button>
            </div>
          </div>

          <p className={`text-[11px] mt-4 font-mono ${user?.has_card ? "text-primary/70" : "text-amber-400/80"}`}>
            {user?.has_card
              ? `✓ A saved card is on file — auto top-ups charge it automatically${liveMode ? "" : " (Stripe test mode: card 4242 4242 4242 4242)"}.`
              : "⚠︎ Demo mode: without a saved card, top-ups are simulated (credits granted without a real charge)."}
          </p>
        </div>
      </section>

      <section className="mt-12 max-w-2xl space-y-6">
        <ReferralCard />
        <ModelStatsCard />
        <SceneAnalyticsCard />
        <ShareClicksCard />
        <ArtistProfileCard />
        <FollowerStatsCard />
        <MockupStatsCard />
      </section>

      <section className="mt-12 max-w-2xl">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="font-display text-2xl font-extrabold flex items-center gap-2"><Receipt className="text-primary" size={22} /> Billing history</h2>
          <div className="flex items-center gap-2">
            <Select value={histType} onValueChange={setHistType}>
              <SelectTrigger className="bg-background border-border h-8 w-[130px] text-xs" data-testid="history-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="pack">Credit packs</SelectItem>
                <SelectItem value="subscription">Subscriptions</SelectItem>
                <SelectItem value="auto_topup">Auto top-ups</SelectItem>
              </SelectContent>
            </Select>
            <Select value={histRange} onValueChange={setHistRange}>
              <SelectTrigger className="bg-background border-border h-8 w-[120px] text-xs" data-testid="history-range-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={exportHistoryCsv} disabled={!filteredHistory.length} className="h-8 border-border gap-1.5" data-testid="export-history-csv"><Download size={14} /> CSV</Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="statement-controls">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5"><FileText size={14} className="text-primary" /> Monthly statement (PDF):</span>
          <input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} className="bg-background border border-border rounded-md h-8 px-2 text-xs font-mono" data-testid="statement-month" />
          <Button variant="outline" size="sm" onClick={downloadStatement} className="h-8 border-border gap-1.5" data-testid="download-statement"><Download size={14} /> Download</Button>
          <Button variant="ghost" size="sm" onClick={emailStatement} className="h-8 gap-1.5 text-primary hover:text-primary" data-testid="email-statement"><Receipt size={14} /> Email me</Button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid="spend-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5"><TrendingUp size={13} /> MONTHLY SPEND</div>
            <div className="flex items-center gap-3 text-[10px] font-mono">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-primary inline-block" /> REAL</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400 inline-block" /> DEMO</span>
            </div>
          </div>
          {topup.monthly_cap > 0 && (
            <div className="mb-3" data-testid="cap-progress">
              <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                <span className="text-muted-foreground">This month: €{monthRealSpend.toFixed(2)} / €{topup.monthly_cap} cap</span>
                {monthRealSpend >= topup.monthly_cap && <span className="text-amber-400 font-semibold" data-testid="cap-reached-badge">AUTO TOP-UP PAUSED</span>}
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full ${monthRealSpend >= topup.monthly_cap ? "bg-amber-400" : "bg-primary"}`} style={{ width: `${Math.min(100, (monthRealSpend / topup.monthly_cap) * 100)}%` }} />
              </div>
            </div>
          )}
          <div className="flex items-end gap-2 h-24">
            {(() => {
              const max = Math.max(1, ...spend.map((m) => m.real + m.demo));
              return spend.map((m) => {
                const total = m.real + m.demo;
                return (
                  <div key={m.month} className="flex-1 h-full flex flex-col items-center justify-end gap-1" data-testid={`spend-bar-${m.month}`} title={`${m.month}: €${m.real.toFixed(2)} real · €${m.demo.toFixed(2)} demo`}>
                    <span className="text-[9px] font-mono text-muted-foreground h-3 leading-3">{total > 0 ? `€${total.toFixed(0)}` : ""}</span>
                    <div className="w-full flex flex-col justify-end rounded-t overflow-hidden" style={{ height: `${Math.max(2, (total / max) * 62)}px` }}>
                      <div className="w-full bg-amber-400" style={{ height: `${total ? (m.demo / total) * 100 : 0}%` }} />
                      <div className="w-full bg-primary" style={{ height: `${total ? (m.real / total) * 100 : 0}%` }} />
                    </div>
                    <span className="text-[9px] font-mono text-muted-foreground/70 leading-3">{m.month.slice(5)}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="billing-history">
          {filteredHistory.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground" data-testid="billing-history-empty">{history.length === 0 ? "No purchases or auto top-ups yet." : "No entries match these filters."}</div>
          ) : (
            <div className="divide-y divide-border">
              {filteredHistory.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={`history-row-${i}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {h.description}
                      {h.auto_topup && <Badge variant="outline" className="text-[9px] border-primary/40 text-primary px-1.5 py-0">AUTO</Badge>}
                      {h.kind === "reward" ? <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-fuchsia-500/40 text-fuchsia-300" data-testid={`history-reward-${i}`}>GIFT</Badge>
                        : <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${h.mode === "real" ? "border-primary/40 text-primary" : "border-amber-500/40 text-amber-400"}`}>{h.mode === "real" ? "REAL" : "DEMO"}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{h.date ? new Date(h.date).toLocaleString() : ""} · +{h.credits} credits</div>
                  </div>
                  <div className="text-sm font-mono font-bold text-right shrink-0">€{h.amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
